import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Chip, Skeleton, alpha, useTheme } from '@mui/material';
import { CloudUpload, Refresh, FiberManualRecord as DotIcon, Cable as CableIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { Schema, ConnectorConfig } from '../../services/api';
import { humanizeSchemaName } from '../../utils/transformationHelpers';
import { CONNECTOR_COLORS } from '../../types/connectorCatalog';

interface HeroSectionProps {
  projectName?: string;
  schemaSet: Schema | null | undefined;
  isLoading: boolean;
  isRefreshing?: boolean;
  onUploadClick: () => void;
  onRefresh: () => void;
  sourceConnector?: ConnectorConfig | null;
  destinationConnector?: ConnectorConfig | null;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  projectName,
  schemaSet,
  isLoading,
  isRefreshing = false,
  onUploadClick,
  onRefresh,
  sourceConnector,
  destinationConnector,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  /** Extract catalog connector id from dlt connector type like "dlt_postgresql_source" */
  const getCatalogId = (connectorType?: string) => {
    const match = connectorType?.match(/^dlt_(.+)_(source|destination)$/);
    return match?.[1];
  };

  const renderConnectorChip = (connector: ConnectorConfig | null | undefined, label: string) => {
    if (!connector) return null;

    const catalogId = getCatalogId(connector.connector_type);
    const brandColor = catalogId ? CONNECTOR_COLORS[catalogId] : theme.palette.primary.main;

    return (
      <Chip
        icon={<DotIcon sx={{ fontSize: 10, color: 'success.main' }} />}
        label={`${label}: ${connector.name}`}
        size="small"
        variant="outlined"
        sx={{
          fontSize: '0.75rem',
          borderColor: alpha(brandColor || theme.palette.primary.main, 0.4),
          cursor: 'pointer',
          '&:hover': { borderColor: brandColor },
        }}
        onClick={() => navigate('/connectors')}
      />
    );
  };

  return (
    <Box
      sx={{
        mb: 4,
        pb: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTop: '3px solid',
        borderImage: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main}) 1`,
        pt: 3,
        px: 0.5,
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
        <Box>
          {isLoading ? (
            <>
              <Skeleton width={240} height={36} />
              <Skeleton width={320} height={24} sx={{ mt: 0.5 }} />
            </>
          ) : (
            <>
              <Typography variant="h4" fontWeight={700}>
                {projectName || 'Project Dashboard'}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                {schemaSet ? (
                  <>
                    <Typography variant="body1" color="text.secondary">
                      Transforming to:
                    </Typography>
                    <Chip
                      label={`${humanizeSchemaName(schemaSet.schema_name)} v${schemaSet.version}`}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No target format configured yet. Uploads will be processed with AI suggestions.
                  </Typography>
                )}
              </Stack>

              {/* Source / Destination connector status */}
              {(sourceConnector || destinationConnector) && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  {renderConnectorChip(sourceConnector, 'Source')}
                  {renderConnectorChip(destinationConnector, 'Dest')}
                </Stack>
              )}
            </>
          )}
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<CableIcon />}
            onClick={() => navigate('/connectors')}
            size="small"
          >
            Connections
          </Button>
          <Button
            variant="outlined"
            startIcon={
              isRefreshing ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Refresh />
              )
            }
            onClick={onRefresh}
            disabled={isLoading || isRefreshing}
            size="small"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            size="large"
            startIcon={<CloudUpload />}
            onClick={onUploadClick}
            sx={{
              px: 3,
              py: 1.2,
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            New Transformation
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        Upload your data files or import from connected sources and AI will map them to your target format automatically.
      </Typography>
    </Box>
  );
};

export default HeroSection;
